import React from "react";
import styled from "styled-components";
import { Container, Section, Eyebrow } from "../components/ui/Container";
import { colors, fonts, media, radius } from "../theme";

const layers = [
  { n: 1, title: "Identity", body: "National ID, selfie, and a verified mobile money number." },
  { n: 2, title: "Cashflow", body: "90 day mobile money inflow and outflow analysis." },
  { n: 3, title: "Stock evidence", body: "Geo tagged photos of stock from the borrower's premises." },
  { n: 4, title: "Local witness", body: "KCCA or trading licence, market chairman, or SACCO vouch." },
  { n: 5, title: "Behavioural", body: "Repeat borrower history, tier migration, and watch list flags." },
];

export const TrustScoring = () => (
  <Section>
    <Container>
      <Head>
        <Eyebrow>Credit scoring & risk</Eyebrow>
        <h2>Five layer verification.<br />That's how we keep repayment near <Hl>98%</Hl>.</h2>
        <p>
          Banks demand land titles. We demand evidence. Five separate signals,
          each verifiable, are weighted into a transparent A to D risk score that
          investors see before they fund.
        </p>
      </Head>

      <Grid>
        {layers.map((l) => (
          <Layer key={l.n}>
            <LNum>{l.n}</LNum>
            <h4>{l.title}</h4>
            <p>{l.body}</p>
          </Layer>
        ))}
      </Grid>

      <Stats>
        <Stat><strong>98%</strong><span>Repayment rate</span></Stat>
        <Stat><strong>90%</strong><span>Re borrow rate</span></Stat>
        <Stat><strong>4</strong><span>Risk priced tiers</span></Stat>
        <Stat><strong>30 to 90 days</strong><span>Typical loan tenor</span></Stat>
      </Stats>
    </Container>
  </Section>
);

const Head = styled.div`
  max-width: 760px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 56px;
  h2 { font-family: ${fonts.display}; }
  p { font-size: 17px; max-width: 640px; }
`;

const Hl = styled.span`
  background: linear-gradient(90deg, ${colors.brand}, ${colors.gold});
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 48px;
  ${media.lg} { grid-template-columns: repeat(2, 1fr); }
  ${media.sm} { grid-template-columns: 1fr; }
`;

const Layer = styled.div`
  border: 1px solid ${colors.line};
  border-radius: ${radius.lg};
  padding: 22px;
  background: white;
  transition: border-color 200ms ease, transform 200ms ease;
  &:hover { border-color: ${colors.brand}; transform: translateY(-3px); }
  h4 { margin-top: 12px; font-family: ${fonts.display}; font-size: 17px; }
  p { font-size: 13.5px; margin-top: 6px; }
`;

const LNum = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${colors.brandSoft};
  color: ${colors.brand};
  font-family: ${fonts.display};
  font-weight: 700;
  font-size: 16px;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border: 1px solid ${colors.line};
  border-radius: ${radius.lg};
  overflow: hidden;
  ${media.md} { grid-template-columns: repeat(2, 1fr); }
`;

const Stat = styled.div`
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-right: 1px solid ${colors.line};
  background: ${colors.surfaceAlt};

  &:last-child { border-right: none; }

  strong {
    font-family: ${fonts.display};
    font-size: 32px;
    font-weight: 800;
    color: ${colors.ink};
  }
  span {
    font-size: 13px;
    color: ${colors.inkSoft};
  }

  ${media.md} {
    border-right: 1px solid ${colors.line};
    border-bottom: 1px solid ${colors.line};
    &:nth-child(2n) { border-right: none; }
    &:nth-last-child(-n+2) { border-bottom: none; }
  }
`;
